import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { CheckCircle, Warning, Clock, Circle, ArrowsClockwise, ArrowClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { fetchMailboxHealth, beginOAuthConnect, oauthProviderForMailbox, type MailboxHealth, type MailboxHealthStatus } from '@/lib/mailboxApi';
import { getApiErrorMessage } from '@/lib/api';
import { OAuthReturnNotice } from '@/components/shared/OAuthReturnNotice';

// Email Sync Health — operational view of every connected mailbox. Mirrors the Calendar
// Sync Health page. Surfaces the data the backend already computes (getMailboxHealth):
// status, last sync, last error, and pending (emails awaiting body fetch = "catching up").

function fmt(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

const HEALTH_STYLES: Record<MailboxHealthStatus, { label: string; cls: string }> = {
  healthy:      { label: 'Healthy',      cls: 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' },
  delayed:      { label: 'Delayed',      cls: 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300' },
  stale:        { label: 'Stale',        cls: 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300' },
  reconnecting: { label: 'Reconnecting', cls: 'bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300' },
  error:        { label: 'Error',        cls: 'bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300' },
  disconnected: { label: 'Disconnected', cls: 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400' },
};

function Stat({ label, value, tone }: { label: string; value: string | number; tone?: string }) {
  return (
    <div className="rounded-xl border border-border/50 bg-card px-4 py-3">
      <div className={`text-xl font-bold ${tone ?? 'text-foreground'}`}>{value}</div>
      <div className="mt-0.5 text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}

function count(rows: MailboxHealth[], s: MailboxHealthStatus): number {
  return rows.filter((r) => r.healthStatus === s).length;
}

export default function EmailSyncHealthPage() {
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['email-sync-health'],
    queryFn: fetchMailboxHealth,
    refetchInterval: 60_000,
  });
  const [reconnecting, setReconnecting] = useState<string | null>(null);

  const rows = data ?? [];
  const [openDetail, setOpenDetail] = useState<string | null>(null);

  // A reconnect of THIS mailbox (the server refuses a different account), returning here.
  async function reconnect(m: MailboxHealth) {
    const provider = oauthProviderForMailbox(m.provider);
    if (!provider) return;
    setReconnecting(m.mailboxId);
    try {
      const url = await beginOAuthConnect(provider, { mailboxId: m.mailboxId, returnTo: 'email-health' });
      window.location.href = url;
    } catch (err) {
      setReconnecting(null);
      toast.error(getApiErrorMessage(err, 'Could not start the reconnection'));
    }
  }

  return (
    <div className="mx-auto max-w-4xl pb-20">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Email Sync Health</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Operational view of every connected mailbox — last sync, errors, and catch-up progress.
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground transition hover:bg-muted disabled:opacity-50"
        >
          <ArrowsClockwise size={12} className={isFetching ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <OAuthReturnNotice connectedText="Sync is restarting." />

      {isLoading ? (
        <div className="h-40 animate-pulse rounded-2xl bg-muted/40" />
      ) : !data ? (
        <p className="text-sm text-muted-foreground">Could not load health data.</p>
      ) : (
        <>
          {/* Summary stats */}
          <div className="mb-6 grid grid-cols-3 gap-3 sm:grid-cols-6">
            <Stat label="Mailboxes" value={rows.length} />
            <Stat label="Healthy" value={count(rows, 'healthy')} tone="text-emerald-600 dark:text-emerald-400" />
            <Stat label="Delayed" value={count(rows, 'delayed') + count(rows, 'stale')} tone={(count(rows, 'delayed') + count(rows, 'stale')) ? 'text-amber-600 dark:text-amber-400' : undefined} />
            <Stat label="Error" value={count(rows, 'error')} tone={count(rows, 'error') ? 'text-red-600 dark:text-red-400' : undefined} />
            <Stat label="Disconnected" value={count(rows, 'disconnected')} tone={count(rows, 'disconnected') ? 'text-slate-600 dark:text-slate-400' : undefined} />
            <Stat label="Catching up" value={rows.reduce((n, r) => n + (r.pendingCount > 0 ? 1 : 0), 0)} />
          </div>

          {/* Mailbox table */}
          <div className="overflow-hidden rounded-2xl border border-border/60 bg-card">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/50 text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-2.5 font-medium">Mailbox</th>
                  <th className="px-4 py-2.5 font-medium">Status</th>
                  <th className="px-4 py-2.5 font-medium">Last sync</th>
                  <th className="px-4 py-2.5 font-medium">Pending</th>
                  <th className="px-4 py-2.5 font-medium">Detail</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">No mailboxes connected yet.</td></tr>
                ) : rows.map((m) => {
                  const s = HEALTH_STYLES[m.healthStatus];
                  const isOpen = openDetail === m.mailboxId;
                  return (
                    <tr key={m.mailboxId} className="border-b border-border/30 last:border-0 align-top">
                      <td className="px-4 py-3 font-medium text-foreground">{m.emailAddress}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${s.cls}`}>
                          {m.healthStatus === 'healthy' && <CheckCircle size={9} weight="bold" />}
                          {(m.healthStatus === 'delayed' || m.healthStatus === 'stale') && <Clock size={9} weight="bold" />}
                          {m.healthStatus === 'error' && <Warning size={9} weight="bold" />}
                          {m.healthStatus === 'disconnected' && <Circle size={9} weight="bold" />}
                          {m.healthStatus === 'reconnecting' && <ArrowsClockwise size={9} weight="bold" />}
                          {s.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {fmt(m.lastSyncAt)}
                        {m.minutesSinceSync !== null && <span className="ml-1 text-[11px]">({m.minutesSinceSync}m ago)</span>}
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {m.pendingCount > 0
                          ? <span className="text-amber-600 dark:text-amber-400">{m.pendingCount} catching up</span>
                          : <span className="text-muted-foreground/60">—</span>}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {/* Human-readable state first — the raw provider error is
                            tucked behind a "Technical details" toggle for engineers. */}
                        <div className="font-medium text-foreground">{m.userTitle}</div>
                        <div className="mt-0.5 text-muted-foreground">{m.userDetail}</div>
                        <div className="mt-1.5 flex items-center gap-3">
                          {m.needsUserAction && (
                            <button
                              type="button"
                              onClick={() => reconnect(m)}
                              disabled={reconnecting === m.mailboxId}
                              className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 font-medium text-foreground transition hover:bg-muted disabled:opacity-50"
                            >
                              <ArrowClockwise size={11} className={reconnecting === m.mailboxId ? 'animate-spin' : ''} /> Reconnect
                            </button>
                          )}
                          {m.lastSyncError && (
                            <button
                              type="button"
                              onClick={() => setOpenDetail(isOpen ? null : m.mailboxId)}
                              className="text-[11px] font-medium text-muted-foreground underline-offset-2 hover:underline"
                            >
                              {isOpen ? 'Hide technical details' : 'Technical details'}
                            </button>
                          )}
                        </div>
                        {isOpen && m.lastSyncError && (
                          <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded-md bg-muted/60 p-2 font-mono text-[11px] text-muted-foreground">
                            {m.lastSyncError}
                          </pre>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <p className="mt-4 text-[11px] text-muted-foreground">
            Recovery is automatic: an expired Gmail/Outlook sync cursor is detected and the mailbox
            re-syncs from a full backfill — no email is lost. A persistent <span className="font-medium">Error</span>/
            <span className="font-medium">Disconnected</span> status means the account needs to be reconnected.
          </p>
        </>
      )}
    </div>
  );
}
